import Image from "next/image";
import { useTranslation } from "next-i18next";
import { IUserStats } from "@/props/IDonation";
import { Popover } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import Icon from "../Icon";
import { formatUSD } from "@/utils";
import Link from "next/link";

const DonationInfo = ({
  isIB,
  isMobile,
  userStats,
  fromEventEnd = false,
}: {
  isIB: boolean;
  isMobile: boolean;
  userStats: IUserStats;
  fromEventEnd?: boolean;
}) => {
  const { t } = useTranslation();
  const isVertical = fromEventEnd || isMobile;
  const padding = isVertical ? "px-8" : "p-6";
  const labelTextSize = isMobile ? "text-base" : "text-2xl";
  const numberTextSize = isMobile ? "text-[28px]" : "text-5xl";

  const renderBlock = (
    title: React.ReactNode,
    value: React.ReactNode,
    hasPopover = false,
    popoverContent?: React.ReactNode,
    noBorder = false,
    hasUSD = false,
    index?: number
  ) => (
    <div
      className={`flex flex-col items-center justify-center border-[#cccccc] py-4 text-center
        ${isVertical ? "w-full" : ""}
        ${noBorder ? "" : isVertical ? "border-b" : "border-r"}
        ${
          fromEventEnd
            ? index !== undefined && index < 3
              ? "basis-[27%]"
              : "basis-[19%]"
            : isIB
            ? "basis-1/4"
            : "basis-1/3"
        }
      `}
    >
      <div className="mb-2 flex items-center">
        <span className={`font-semibold text-[#f6d7d6] ${labelTextSize}`}>{title}</span>
        {hasPopover && (
          <Popover content={<div className="w-[244px]">{popoverContent}</div>} trigger="hover">
            <QuestionCircleOutlined className="ml-2 cursor-pointer" style={{ fontSize: isMobile ? 16 : 24 }} />
          </Popover>
        )}
      </div>
      <span className={`font-medium text-white ${numberTextSize}`}>
        {value}
        {hasUSD && <span className={`ml-2 font-semibold text-[#f6d7d6] ${labelTextSize}`}>USD</span>}
      </span>
    </div>
  );

  return (
    <div
      className={`relative flex flex-wrap overflow-hidden rounded-[32px] text-white ${padding} 
        ${fromEventEnd && isMobile ? "h-[430px] " : fromEventEnd ? "h-full " : isMobile ? "h-full " : "h-[207px] "}
        ${fromEventEnd ? "w-full flex-col items-stretch" : isMobile ? "flex-col items-center" : "flex-row items-center"}
      `}
    >
      <div className="absolute inset-0 -z-10">
        <Image
          src={isMobile ? "/images/csr/race_bg_mobile.webp" : "/images/csr/race_bg_2.webp"}
          alt="Race background"
          fill
          className="object-cover"
          quality={100}
        />
      </div>
      {isIB &&
        userStats?.rank !== undefined &&
        userStats?.rank !== null &&
        renderBlock(
          t("csr.this_round_rank"),
          <>
            {userStats.rank}
            <span className={`ml-2 font-semibold text-[#f6d7d6] ${labelTextSize}`}>{t("csr.rank")}</span>
          </>
        )}
      {renderBlock(
        isIB ? t("csr.ib_investment_donation") : t("csr.client_investment_donation"),
        formatUSD(userStats?.totalTradingDonationAmountUsd),
        true,
        isIB ? t("csr.ib_investment_donation_desc") : t("csr.client_investment_donation_desc"),
        false,
        true,
        0
      )}
      {renderBlock(
        isIB ? t("csr.ib_dream_donation") : t("csr.client_dream_donation"),
        formatUSD(userStats?.totalDreamPointsDonationAmountUsd),
        true,
        isIB ? t("csr.ib_dream_donation_desc") : t("csr.client_dream_donation_desc"),
        false,
        true,
        1
      )}
      {renderBlock(
        t("csr.donation_total"),
        formatUSD(userStats?.totalDonationAmountUsd),
        false,
        null,
        fromEventEnd ? false : true,
        true,
        2
      )}
      {!isIB &&
        fromEventEnd &&
        renderBlock(
          <Link href="/user/points?tab=donation" className="-mb-2 flex items-center">
            <span className="font-semibold text-white">{t("csr.check_donation")}</span>
            <Icon name="arrow-right-gray" isMobile size={16} className="ml-2" />
          </Link>,
          null,
          false,
          null,
          true,
          false,
          3
        )}
    </div>
  );
};
export default DonationInfo;
